import { OutlookAvailabilityTime } from './OutlookAvailabilityTime'
import { DateTimeTable, DateTimeTableData } from './DataTimeTable'

/**
 * SendAvailabilityEvent interface
 * Represents a time slot with start and end times
 */
export interface SendAvailabilityEvent {
  startTime: number // milliseconds
  endTime: number // milliseconds
}

/**
 * SendAvailabilityItems interface
 * Collection of availability events with timezone information
 */
export interface SendAvailabilityItems {
  items: SendAvailabilityEvent[]
  timeZoneId: string
}

/**
 * JSONDataAndTimeZone interface
 * Result of conversion containing JSON string and timezone
 */
export interface JSONDataAndTimeZone {
  json: string
  gmtTimeZone: string
}

/**
 * OutlookDateTimeHelper class
 * Provides utility functions for converting between Outlook date-time formats
 * and application-specific formats
 */
export class OutlookDateTimeHelper {
  /**
   * Parse JSON string to DateTimeTable array
   * json -> List<DateTimeTable>
   */
  static getOutlookDateTimeTablesFromJson(json: string): DateTimeTable[] | null {
    try {
      const data: DateTimeTableData[] = JSON.parse(json)
      return data.map((tableData) => DateTimeTable.fromJSON(tableData))
    } catch (error) {
      console.error('Failed to parse OutlookDateTimeTables from JSON:', error)
      return null
    }
  }

  /**
   * Convert DateTimeTable array to JSON string
   * List<DateTimeTable> -> json
   */
  static getJsonFromOutlookDateTimeTables(dateTimeTables: DateTimeTable[]): string {
    try {
      const data = dateTimeTables.map((table) => table.toJSON())
      const json = JSON.stringify(data, null, 2)
      console.log('Converted createJson:', json)
      return json
    } catch (error) {
      console.error('Failed to convert OutlookDateTimeTables to JSON:', error)
      throw error
    }
  }

  /**
   * Convert DateTimeTable array to SendAvailabilityEvent array
   * List<DateTimeTable> -> Array<SendAvailabilityEvent>
   */
  static convertOutlookDateTimeTables2SendAvailabilityEvents(
    dateTimeTables: DateTimeTable[] | null,
  ): SendAvailabilityEvent[] {
    if (!dateTimeTables) {
      return []
    }

    const availabilityEventList: SendAvailabilityEvent[] = []

    for (const dateTimeTable of dateTimeTables) {
      const events = OutlookDateTimeHelper.convertOutlookAvailabilityTimes2SendAvailabilityEvents(
        dateTimeTable.timeSlots,
      )
      availabilityEventList.push(...events)
    }

    return availabilityEventList
  }

  /**
   * Convert OutlookAvailabilityTime array to SendAvailabilityEvent array
   * List<OutlookAvailabilityTime> -> Array<SendAvailabilityEvent>
   */
  static convertOutlookAvailabilityTimes2SendAvailabilityEvents(
    timeSlots: OutlookAvailabilityTime[],
  ): SendAvailabilityEvent[] {
    const availabilityEventList: SendAvailabilityEvent[] = []

    for (const outlookAvailabilityTime of timeSlots) {
      const startDateCalendar = outlookAvailabilityTime.getStartDateCalendar()
      const endDateCalendar = outlookAvailabilityTime.getEndDateCalendar()

      availabilityEventList.push({
        startTime: startDateCalendar.getTime(),
        endTime: endDateCalendar.getTime(),
      })
    }

    return availabilityEventList
  }

  /**
   * Convert SendAvailabilityItems to Outlook-style dateTimeTableJson
   * SendAvailabilityItems -> outlook style dateTimeTableJson
   */
  static getJsonDataAndTimeZoneFromSendAvailabilityItems(
    sendAvailabilityItems: SendAvailabilityItems,
  ): JSONDataAndTimeZone {
    // key: dateLabel, value: OutlookAvailabilityTime[]
    const timeLabelMap = new Map<string, OutlookAvailabilityTime[]>()
    let gmtTimeZone = ''

    for (const sendAvailabilityEvent of sendAvailabilityItems.items) {
      // Create Date objects with timezone
      const startCalendar = new Date(sendAvailabilityEvent.startTime)
      const endCalendar = new Date(sendAvailabilityEvent.endTime)

      // Create OutlookAvailabilityTime
      const availabilityTime = new OutlookAvailabilityTime(startCalendar, endCalendar)

      // Group time slots by date (uses settings-based formatting)
      const dayLabel = availabilityTime.getDayLabel()
      const existingTimeSlots = timeLabelMap.get(dayLabel)

      if (existingTimeSlots) {
        existingTimeSlots.push(availabilityTime)
      } else {
        timeLabelMap.set(dayLabel, [availabilityTime])
      }

      // Store timezone information
      gmtTimeZone = availabilityTime.timeZone
    }

    // Convert map to DateTimeTable array
    const dateTimeTableList: DateTimeTable[] = Array.from(timeLabelMap.entries()).map(
      ([dayLabel, timeSlots]) => new DateTimeTable(dayLabel, timeSlots),
    )

    // Convert to JSON
    const json = OutlookDateTimeHelper.getJsonFromOutlookDateTimeTables(dateTimeTableList)

    return {
      json,
      gmtTimeZone,
    }
  }

  /**
   * Group SendAvailabilityEvents by date
   * Helper function to organize events by day
   * Uses settings-based formatting
   */
  static groupEventsByDate(
    events: SendAvailabilityEvent[],
    // timeZoneId: string,
  ): Map<string, SendAvailabilityEvent[]> {
    const groupedEvents = new Map<string, SendAvailabilityEvent[]>()

    for (const event of events) {
      const date = new Date(event.startTime)
      // Create a temporary OutlookAvailabilityTime to get consistent formatting
      const tempAvailability = new OutlookAvailabilityTime(date, date)
      const dayLabel = tempAvailability.getDayLabel()

      const existingEvents = groupedEvents.get(dayLabel)
      if (existingEvents) {
        existingEvents.push(event)
      } else {
        groupedEvents.set(dayLabel, [event])
      }
    }

    return groupedEvents
  }

  /**
   * Sort time slots by start time
   */
  static sortTimeSlots(timeSlots: OutlookAvailabilityTime[]): OutlookAvailabilityTime[] {
    return timeSlots.sort((a, b) => {
      const aStart = a.getStartDateCalendar().getTime()
      const bStart = b.getStartDateCalendar().getTime()
      return aStart - bStart
    })
  }

  /**
   * Validate time slot - ensure start time is before end time
   */
  static isValidTimeSlot(timeSlot: OutlookAvailabilityTime): boolean {
    const startTime = timeSlot.getStartDateCalendar().getTime()
    const endTime = timeSlot.getEndDateCalendar().getTime()
    return startTime < endTime
  }

  /**
   * Merge overlapping time slots
   */
  static mergeOverlappingSlots(timeSlots: OutlookAvailabilityTime[]): OutlookAvailabilityTime[] {
    if (timeSlots.length === 0) {
      return []
    }

    // Sort by start time
    const sorted = OutlookDateTimeHelper.sortTimeSlots(timeSlots)
    const merged: OutlookAvailabilityTime[] = [sorted[0]]

    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i]
      const lastMerged = merged[merged.length - 1]

      const lastEnd = lastMerged.getEndDateCalendar().getTime()
      const currentStart = current.getStartDateCalendar().getTime()

      if (currentStart <= lastEnd) {
        // Overlapping - merge by extending end time if needed
        const currentEnd = current.getEndDateCalendar().getTime()
        if (currentEnd > lastEnd) {
          const newStart = lastMerged.getStartDateCalendar()
          const newEnd = current.getEndDateCalendar()
          merged[merged.length - 1] = new OutlookAvailabilityTime(newStart, newEnd)
        }
      } else {
        // Not overlapping - add as new slot
        merged.push(current)
      }
    }

    return merged
  }
}

// Export all interfaces and classes
export * from './OutlookAvailabilityTime'
export * from './DataTimeTable'
